import { useState, useEffect } from 'react';
import './Pages.css';
import CTA from '../sections/CTA';
import staticPosts from '../data/posts';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Full article page for a single blog post — reached via "Read More" from
// the home page preview and the blog listing page (#/blog/<id>).
export default function BlogDetailPage({ postId }) {
  const [post, setPost]     = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound

  useEffect(() => {
    setStatus('loading');
    setPost(null);

    const fallback = staticPosts.find(p => String(p.id) === String(postId));

    fetch(`${API_BASE}/api/blog/${postId}`)
      .then(r => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(data => {
        const normalized = {
          ...data,
          img: data.img && data.img.startsWith('/uploads/') ? `${API_BASE}${data.img}` : data.img,
        };
        setPost(normalized);
        setStatus('ok');
      })
      .catch(() => {
        if (fallback) {
          setPost(fallback);
          setStatus('ok');
        } else {
          setStatus('notfound');
        }
      });

    // Related posts — pull from the static list (fast, no extra request);
    // good enough for a "you might also like" rail.
    setRelated(staticPosts.filter(p => String(p.id) !== String(postId)).slice(0, 3));
  }, [postId]);

  if (status === 'loading') {
    return (
      <>
        <div className="page-top-spacer" />
        <section className="blog-detail-section">
          <p className="blog-detail-loading">Loading article…</p>
        </section>
      </>
    );
  }

  if (status === 'notfound' || !post) {
    return (
      <>
        <div className="page-top-spacer" />
        <section className="blog-detail-section">
          <div className="blog-detail-notfound">
            <h2>Post not found</h2>
            <p>The article you're looking for doesn't exist or may have been removed.</p>
            <a href="#/blog" className="btn-ghost">← Back to Blog</a>
          </div>
        </section>
      </>
    );
  }

  const body = post.content && post.content.trim() ? post.content : post.desc;
  const paragraphs = body.split('\n').map(s => s.trim()).filter(Boolean);

  return (
    <>
      <div className="page-top-spacer" />
      <article className="blog-detail-section">
        <div className="blog-detail-header reveal">
          <a href="#/blog" className="blog-detail-back">← Back to Blog</a>
          <span className="blog-cat">{post.cat}</span>
          <h1 className="blog-detail-title">{post.title}</h1>
          <div className="blog-meta blog-detail-meta">
            <span className="blog-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {post.date}
            </span>
          </div>
        </div>

        <div className="blog-detail-img reveal">
          <img src={post.img} alt={post.title} />
        </div>

        <div className="blog-detail-body reveal">
          {paragraphs.map((para, i) => <p key={i}>{para}</p>)}
        </div>

        {related.length > 0 && (
          <div className="blog-detail-related reveal">
            <h3>You might also like</h3>
            <div className="blog-grid">
              {related.map(p => (
                <article key={p.id} className="blog-card">
                  <div className="blog-img">
                    <img src={p.img} alt={p.title} loading="lazy" />
                  </div>
                  <div className="blog-body">
                    <span className="blog-cat">{p.cat}</span>
                    <h3>{p.title}</h3>
                    <p>{p.desc}</p>
                    <a href={`#/blog/${p.id}`} className="blog-read">Read More →</a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </article>
      <CTA />
    </>
  );
}
